import { useState, useCallback, useRef } from 'react';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { Flex } from 'foundations-components/components/Flex';
import ImageLoadingTypes from 'ContentUtils/constants/ImageLoadingTypes';
import styled from 'foundations-theming/utils';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import LoadingSelect from 'ContentComponents/components/cmv2/media/LoadingSelect';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PopoverContent = styled.div.withConfig({
  displayName: "ImageTool__PopoverContent"
})(({
  theme
}) => ({
  padding: theme.space['600'],
  width: '320px'
}));
const Header = styled.div.withConfig({
  displayName: "ImageTool__Header"
})(({
  theme
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.space['500']
}));
const Title = styled.h3.withConfig({
  displayName: "ImageTool__Title"
})(({
  theme
}) => Object.assign({
  margin: 0
}, theme.typography['body-200'], {
  fontWeight: 600
}));
const CloseButton = styled.button.withConfig({
  displayName: "ImageTool__CloseButton"
})(({
  theme
}) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '20px',
  lineHeight: 1,
  padding: theme.space['100'],
  color: theme.color['icon-core-subtle'],
  '&:hover': {
    color: theme.color['icon-core-default']
  }
}));
const ButtonWrapper = styled.div.withConfig({
  displayName: "ImageTool__ButtonWrapper"
})({
  display: 'inline-block'
});
export function ImageTool({
  info,
  onFieldChange,
  onClose
}) {
  var _info$toolContext;
  const onReplace = (_info$toolContext = info.toolContext) === null || _info$toolContext === void 0 ? void 0 : _info$toolContext.onReplace;
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsButtonRef = useRef(null);
  const currentValue = info.currentValue;
  const field = info.field;
  const [pendingAlt, setPendingAlt] = useState(currentValue.alt || '');
  const [pendingLoading, setPendingLoading] = useState(currentValue.loading || ImageLoadingTypes.DISABLED);
  const handleReplaceClick = useCallback(() => {
    onClose();
    onReplace === null || onReplace === void 0 || onReplace();
  }, [onClose, onReplace]);
  const handleSettingsOpen = useCallback(() => {
    setPendingAlt(currentValue.alt || '');
    setPendingLoading(currentValue.loading || ImageLoadingTypes.DISABLED);
    setIsSettingsOpen(true);
  }, [currentValue.alt, currentValue.loading]);
  const handleSettingsClose = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);
  const handleAltChange = useCallback(e => {
    setPendingAlt(e.target.value);
  }, []);
  const handleLazyLoadingChange = useCallback(loadingType => {
    setPendingLoading(loadingType || ImageLoadingTypes.DISABLED);
  }, []);
  const handleApply = useCallback(() => {
    const updatedValue = Object.assign({}, currentValue, {
      alt: pendingAlt,
      loading: pendingLoading
    });
    onFieldChange(info.path, updatedValue);
    setIsSettingsOpen(false);
  }, [currentValue, pendingAlt, pendingLoading, onFieldChange, info.path]);
  const handleCancel = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);
  const handleOpenChange = useCallback(e => {
    if (!e.target.value) {
      handleSettingsClose();
    }
  }, [handleSettingsClose]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [onReplace && /*#__PURE__*/_jsx(Button, {
      size: "small",
      use: "secondary",
      onClick: handleReplaceClick,
      children: I18n.text('inpage.fieldSettingsToolbar.imageReplace.label')
    }), /*#__PURE__*/_jsx(ButtonWrapper, {
      ref: settingsButtonRef,
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: I18n.text('inpage.fieldSettingsToolbar.imageSettings.label'),
        children: /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          use: "secondary",
          onClick: handleSettingsOpen,
          children: /*#__PURE__*/_jsx(SettingsIcon, {})
        })
      })
    }), settingsButtonRef.current && /*#__PURE__*/_jsx(Popover, {
      target: settingsButtonRef.current,
      open: isSettingsOpen,
      placement: "bottom",
      arrowSize: "medium",
      autoPlacement: true,
      onOpenChange: handleOpenChange,
      content: /*#__PURE__*/_jsxs(PopoverContent, {
        "data-dropdown-menu": "true",
        children: [/*#__PURE__*/_jsxs(Header, {
          children: [/*#__PURE__*/_jsx(Title, {
            children: I18n.text('inpage.fieldSettingsToolbar.imageSettings.title')
          }), /*#__PURE__*/_jsx(CloseButton, {
            onClick: handleSettingsClose,
            type: "button",
            "aria-label": "Close",
            children: "\xD7"
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "400",
          children: [/*#__PURE__*/_jsx(FormControl, {
            label: /*#__PURE__*/_jsx(FormLabel, {
              children: I18n.text('inpage.fieldSettingsToolbar.imageSettings.altText')
            }),
            children: /*#__PURE__*/_jsx(TextInput, {
              value: pendingAlt,
              onChange: handleAltChange
            })
          }), field.show_loading && /*#__PURE__*/_jsx(LoadingSelect, {
            onChange: handleLazyLoadingChange,
            readOnly: false,
            useOnDark: false,
            value: pendingLoading
          }), /*#__PURE__*/_jsxs(Flex, {
            gap: "200",
            marginBlockStart: "200",
            children: [/*#__PURE__*/_jsx(Button, {
              use: "primary",
              onClick: handleApply,
              children: I18n.text('inpage.fieldSettingsToolbar.imageSettings.apply')
            }), /*#__PURE__*/_jsx(Button, {
              use: "secondary",
              onClick: handleCancel,
              children: I18n.text('inpage.fieldSettingsToolbar.imageSettings.cancel')
            })]
          })]
        })]
      })
    })]
  });
}